import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { provideMarkdown } from 'ngx-markdown';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

// Angular Material imports
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';

import { routes } from './app.routes';
import { isAuthBridgeWindow } from './config/auth-bridge.config';
import { LOGIN_SCOPES, PROTECTED_RESOURCES } from './config/auth-scopes.config';
import { msalConfig } from './config/msal.config';
import { apiErrorInterceptor } from './interceptors/api-error.interceptor';
import { authInterceptor } from './interceptors/auth.interceptor';

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: LOGIN_SCOPES,
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap: PROTECTED_RESOURCES,
  };
}

let msalInstance: IPublicClientApplication | undefined;

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection(),
    provideRouter(routes),
    provideAnimationsAsync(),
    // apiErrorInterceptor is listed first so it is outermost: authInterceptor still sees the raw
    // HttpErrorResponse, and subscribers receive the ApiError it is turned into.
    provideHttpClient(withInterceptors([apiErrorInterceptor, authInterceptor])),
    provideMarkdown(),
    provideCharts(withDefaultRegisterables()),
    // Angular Material modules
    importProvidersFrom(
      MatButtonModule,
      MatCardModule,
      MatChipsModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MatIconModule,
      MatListModule,
      MatPaginatorModule,
      MatProgressSpinnerModule,
      MatSidenavModule,
      MatSlideToggleModule,
      MatSortModule,
      MatTableModule,
    ),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalBroadcastService,
    provideAppInitializer(() => {
      // The bridge page must not touch MSAL APIs (see RedirectComponent):
      // initialize() runs cache migration and cross-tab subscriptions, which
      // have no purpose inside an auth popup that closes moments later.
      if (isAuthBridgeWindow()) {
        return;
      }
      return inject(MsalService).instance.initialize();
    }),
  ],
};
